/**
 * @file MembershipModal - 会员订阅弹窗
 *
 * 基于 PreviewDrawer 封装的会员订阅选择弹窗
 * 复用 PlanContainer、QuestionContainer 等子组件
 */

import React, {useState, useMemo, useCallback, useEffect} from 'react';
import styles from './index.module.less';
import {useMemberPlans, useMembershipQuestions, refreshMembershipData} from './hooks/useMemberShip';
// 复用现有组件
import {PlanContainer} from './PlanContainer';
import {QuestionContainer} from './QuestionContainer';
import {PaymentModal} from './PaymentModal';
import {PlanConfirmModal} from './PlanConfirmModal';
import {getPrice} from './utils/priceHelper';
import {useMembershipTracking} from './hooks/useMembershipTracking';
import {TrackEvents} from '@/services/tracking/constant';
import Segmented from '@/components/common/SegmentedV2';
import PreviewDrawer from '@/components/PreviewDrawer';
import StatusView from '@/components/common/StatusView';
import Icon from '@/components/common/Icon';
import {createCx} from '@/utils/cx';
import {useModalStore} from '@/store/useModalStore';
import {openPointsRechargeModal} from '@/commerce/modals/openModal';
import {PointsRechargeModalSource} from '@/commerce/modals/PointsRechargeModal/types';
import {SubscriptionTypeValue} from '@/services/commerce/type';
import type {SubscriptionType} from '@/services/commerce/type';
import {OrderTypeValue} from '@/services/commerce/membership/interface';
import type {MemberLevelItem} from '@/services/commerce/membership/interface';
import {useMemberInfo} from '@/commerce/benefit/hooks/useMemberInfo';
import {usePaymentCheck} from '@/commerce/payment/hooks/usePaymentCheck';
import {useMemberExtShowLog} from '@/hooks/memberLogExt';

const cx = createCx(styles);

const MemberSubscribeModal: React.FC = () => {
    // 弹窗状态
    const open = useModalStore((s) => s.membershipModalOpen);
    const setMembershipModalOpen = useModalStore((s) => s.setMembershipModalOpen);
    const onClose = useCallback(() => {
        setMembershipModalOpen(false);
    }, [setMembershipModalOpen]);

    // 用户当前订阅周期类型
    const {memberInfo} = useMemberInfo();
    const userSubscriptionType = memberInfo?.subscriptionType;

    // 从 membership 中 获取会员套餐数据
    const {subscriptionOptions, data, isLoading, isError} = useMemberPlans();
    // 获取问题数据
    const {data: quesTipsData} = useMembershipQuestions();

    // 操作选中的订阅类型
    const [activeTab, setActiveTab] = useState<SubscriptionType>(
        userSubscriptionType ?? SubscriptionTypeValue.AUTO_MONTHLY,
    );

    // 根据当前选中的订阅类型，获取对应的会员等级配置对象
    const plan = useMemo(() => {
        return data?.subscriptionTypes.find((option) => option.subscriptionType === activeTab);
    }, [activeTab, data]);

    // 操作选中的会员等级
    const handleTabChange = useCallback(
        (value: unknown) => {
            setActiveTab(value as SubscriptionType);
        },
        [setActiveTab],
    );

    // 订阅选项段选择器配置 - 展示订阅类型名称 + 标签
    const segmentedOptionsItems = useMemo(() => {
        return (
            subscriptionOptions?.map((item) => ({
                label: (
                    <div className={item.value === activeTab ? cx('selectedItem') : ''}>
                        {item.label} <span className={cx('tag')}>{item.tag}</span>
                    </div>
                ),
                value: item.value,
            })) ?? []
        );
    }, [subscriptionOptions, activeTab]);

    // 缓存选中的套餐配置
    const [selectedLevel, setSelectedLevel] = useState<MemberLevelItem | null>(null);

    // 来源渠道
    const setMembershipModalSource = useModalStore((s) => s.setMembershipModalSource);

    // 打点逻辑
    const {sendClickLog, sendShowLog} = useMembershipTracking({selectedLevel, activeTab});

    // 弹窗展现打点
    useMemberExtShowLog(
        open && {
            value: TrackEvents.PAGE_MEMBERSHIP_SUBSCRIPTION_POP,
        },
        sendShowLog,
    );

    // 弹窗关闭时取消打点来源
    useEffect(() => {
        if (!open) {
            setMembershipModalSource(null);
        }
    }, [open, setMembershipModalSource]);

    // 确认套餐变更弹窗状态
    const [planConfirmModalOpen, setPlanConfirmModalOpen] = useState(false);
    const closePlanConfirmModal = useCallback(() => {
        setPlanConfirmModalOpen(false);
    }, [setPlanConfirmModalOpen]);

    // 支付弹窗状态
    const [paymentModalOpen, setPaymentModalOpen] = useState(false);
    const closePaymentModal = useCallback(() => {
        setPaymentModalOpen(false);
    }, [setPaymentModalOpen]);

    const {mutateAsync: checkPayment} = usePaymentCheck();
    // 支付拦截逻辑（当前支付价格和suk价格是否一致）
    const handlePaymentCheck = useCallback(
        ({selectedLevel, passFn}: {selectedLevel: MemberLevelItem; passFn: () => void}) => {
            void checkPayment({
                skuId: selectedLevel.sku.skuId,
                payAmount: getPrice(
                    selectedLevel,
                    selectedLevel.orderType === OrderTypeValue.UPGRADE,
                ),
            }).then((res) => {
                if (res.pass) {
                    passFn();
                } else {
                    // 刷新会员等级数据
                    refreshMembershipData();
                }
            });
        },
        [checkPayment],
    );

    // 触发打开支付or套餐更换确认弹窗
    const handleLevelClick = useCallback(
        (level: MemberLevelItem) => {
            handlePaymentCheck({
                selectedLevel: level,
                passFn: () => {
                    // 根据 plan.orderType 判断是否需要确认套餐变化
                    setSelectedLevel(level);
                    // 如果当前用户有订阅都需要展示确认弹窗
                    if (userSubscriptionType && userSubscriptionType > 0) {
                        // 打开套餐变化确认弹窗
                        setPlanConfirmModalOpen(true);
                    } else {
                        // 打开支付弹窗
                        setPaymentModalOpen(true);
                    }
                },
            });
            // 点击打点
            sendClickLog({
                value: TrackEvents.MEMBERSHIP_MODAL_SUBSCRIBE_BTN,
                ext: {
                    action: level.orderType,
                    // eslint-disable-next-line camelcase
                    targeted_level: level.memberLevel,
                    // eslint-disable-next-line camelcase
                    targeted_period: activeTab,
                },
            });
        },
        [
            setPaymentModalOpen,
            handlePaymentCheck,
            setPlanConfirmModalOpen,
            sendClickLog,
            userSubscriptionType,
            activeTab,
        ],
    );

    // 处理确认支付逻辑
    const handleConfirmPay = useCallback(() => {
        selectedLevel &&
            handlePaymentCheck({
                selectedLevel: selectedLevel,
                passFn: () => {
                    // 关闭确认弹窗
                    setPlanConfirmModalOpen(false);
                    // 打开支付弹窗
                    setPaymentModalOpen(true);
                },
            });
        // 打点
        sendClickLog({
            page: TrackEvents.MEMBERSHIP_PAY_CONFIRM_MODAL,
            value: TrackEvents.MEMBERSHIP_PAY_CONFIRM_MODAL_CONFIRM_BTN,
        });
    }, [setPlanConfirmModalOpen, handlePaymentCheck, sendClickLog, selectedLevel]);

    // 支付弹窗展现打点
    useMemberExtShowLog(
        paymentModalOpen && {
            page: TrackEvents.MEMBERSHIP_PAY_QRCODE_MODAL,
            value: TrackEvents.MEMBERSHIP_PAY_QRCODE_MODAL,
        },
        sendShowLog,
    );

    // 二次确认弹窗展现打点
    useMemberExtShowLog(
        planConfirmModalOpen && {
            page: TrackEvents.MEMBERSHIP_PAY_CONFIRM_MODAL,
            value: TrackEvents.MEMBERSHIP_PAY_CONFIRM_MODAL,
        },
        sendShowLog,
    );

    // 单独购买积分
    const handleOpenPointsRechargeModal = useCallback(() => {
        openPointsRechargeModal(
            PointsRechargeModalSource.MEMBERSHIP_SUBSCRIPTION_PAGE_PURCHASE_POINTS_SEPARATELY_BTN,
        );
        sendClickLog({
            value: TrackEvents.MEMBERSHIP_MODAL_BUY_POINTS_BTN,
        });
    }, [sendClickLog]);

    return (
        <PreviewDrawer open={open} onClose={onClose} rootClassName={cx('subscribeDrawer')}>
            {isLoading || isError ? (
                <StatusView status={isLoading ? 'loading' : 'error'} />
            ) : (
                <div className={cx('content')}>
                    {/* 头部 */}
                    <div className={cx('header')}>
                        <div className={cx('title')}>订阅会员，畅享更多权益</div>
                        <p className={cx('subtitle')}>
                            <span>
                                {plan?.levels.length ?? 4}档会员等级，14项独有权益，让创作效率倍增
                            </span>

                            <div className={cx('btnText')} onClick={handleOpenPointsRechargeModal}>
                                <span>单独购买积分</span>
                                <Icon name="AijBasicYou1" size={16} />
                            </div>
                        </p>
                    </div>

                    {/* 订阅类型切换 */}
                    <div className={cx('subscriptionSegmented')}>
                        <Segmented
                            value={activeTab}
                            onChange={handleTabChange}
                            options={segmentedOptionsItems}
                        />
                    </div>
                    {/* 会员等级容器 */}
                    <PlanContainer plan={plan} onClick={handleLevelClick} />
                    {/* 常见问题 */}
                    <QuestionContainer faqs={quesTipsData?.questions ?? []} />
                </div>
            )}
            {/* 支付弹窗 - 覆盖在上方 */}
            {selectedLevel && (
                <PaymentModal
                    open={paymentModalOpen}
                    subscriptionType={activeTab}
                    subscriptionName={plan?.typeName ?? ''}
                    level={selectedLevel}
                    onClose={closePaymentModal}
                />
            )}
            {/* 套餐变化信息确认弹窗 - 覆盖在上方 */}
            {selectedLevel && (
                <PlanConfirmModal
                    open={planConfirmModalOpen}
                    subscriptionName={plan?.typeName ?? ''}
                    level={selectedLevel}
                    onConfirm={handleConfirmPay}
                    onClose={closePlanConfirmModal}
                />
            )}
        </PreviewDrawer>
    );
};

export default MemberSubscribeModal;
